import { useState, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getImnooApiKey, updateImnooApiKey } from '@reducers/membership';
import type { ImnooApiKey } from '@api/domain/types/membership';

export interface UseManageImnooReturn {
  imnooApiKey: string;
  setImnooApiKey: (value: string) => void;
  loading: boolean;
  imnooApiKeyInputDisabled: boolean;
  onUpdateImnooApiKey: () => Promise<void>;
}

export const useManageImnoo = (): UseManageImnooReturn => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [originalImnooApiKey, setOriginalImnooApiKey] = useState<string>('');
  const [imnooApiKey, setImnooApiKey] = useState<string>('');

  useFocusEffect(
    useCallback(() => {
      (async () => {
        const apiKeys = (await dispatch(getImnooApiKey())) as ImnooApiKey[] | undefined;
        if (apiKeys?.length) {
          const apiKey = apiKeys[0]?.apiKey;
          setImnooApiKey(apiKey);
          setOriginalImnooApiKey(apiKey);
        }
      })();
    }, []),
  );

  const isApiKeyInputDirty = imnooApiKey !== originalImnooApiKey;
  const imnooApiKeyInputDisabled = !isApiKeyInputDirty || loading || imnooApiKey.length < 25;

  const onUpdateImnooApiKey = useCallback(async () => {
    setLoading(true);
    const response = await dispatch(updateImnooApiKey({ apiKey: imnooApiKey }));
    if (response && !response.isError) {
      setOriginalImnooApiKey(imnooApiKey);
    }
    setLoading(false);
  }, [dispatch, imnooApiKey]);

  return {
    imnooApiKey,
    setImnooApiKey,
    loading,
    imnooApiKeyInputDisabled,
    onUpdateImnooApiKey,
  };
};
